import {Component, inject} from '@angular/core';
import {AsyncPipe} from "@angular/common";
import {Router} from "@angular/router";
import {MatButtonModule} from "@angular/material/button";
import {MatMenuModule} from "@angular/material/menu";
import {MatIcon} from "@angular/material/icon";
import {Observable} from "rxjs";
import {GameService} from "../../services/game.service";
import {ActiveUserService} from "../../services/active-user.service";
import {NavScopeService} from "../../services/nav-scope.service";
import {NavScope} from "../../enum/NavScope";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";

/**
 * Persistent scope switcher for the header (Release 1.19 IA pass, Stage 3).
 *
 * Lets an admin move between "All Games" (global scope) and a specific game without
 * going back to the landing page. Changing the active game is all it needs to do —
 * the side-nav re-scopes reactively off the active game (see NavScopeService).
 *
 * "All Games" is offered only to admins who actually have a global scope (Causable
 * admins or cross-game permission holders). The switcher hides entirely when there is
 * nothing to switch between (a single-game admin with one game and no global access).
 */
@Component({
  selector: 'app-scope-switcher',
  standalone: true,
  imports: [AsyncPipe, MatButtonModule, MatMenuModule, MatIcon],
  templateUrl: './scope-switcher.component.html',
  styleUrl: './scope-switcher.component.scss',
})
export class ScopeSwitcherComponent {
  private gameService = inject(GameService);
  private activeUserService = inject(ActiveUserService);
  private navScopeService = inject(NavScopeService);
  private router = inject(Router);

  public activeGame$: Observable<IGameQueryResult | undefined> = this.gameService.selectActiveGame$();
  public availableGames$: Observable<IGameQueryResult[]> = this.gameService.selectAllAvailableGames();

  /** Whether the "All Games" (global) option should be offered. */
  public canAccessGlobal(): boolean {
    const user = this.activeUserService.activeUser();
    return user.CausableAdmin || (user.CrossGamePermissions?.length ?? 0) > 0;
  }

  /** Whether there is anything worth switching between. */
  public isVisible(games: IGameQueryResult[] | null): boolean {
    return this.canAccessGlobal() || (games?.length ?? 0) > 1;
  }

  /**
   * The active game is updated before the counterpart is looked up: that permission check is per-game, so it
   * has to be asked about the game being switched to.
   */
  public async selectGame(game: IGameQueryResult): Promise<void> {
    const current_url = this.router.url;

    this.gameService.updateActiveGame(game);

    const counterpart_route = this.navScopeService.counterpartRouteForUrl(current_url, NavScope.GameOnly);

    await this.router.navigateByUrl(`${game.Id}/${counterpart_route ?? 'active-game-instances'}`);
  }

  public async selectGlobal(): Promise<void> {
    const current_url = this.router.url;

    this.gameService.clearActiveGame();

    const counterpart_route = this.navScopeService.counterpartRouteForUrl(current_url, NavScope.GlobalOnly);

    await this.router.navigateByUrl(counterpart_route ? `/${counterpart_route}` : '/');
  }
}
